import React, { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { API_BASE, LiveBoardRow } from '../org/scoring-types';
import useWebSocketHandler from '../wsHookHandler';

type ScoringClass = 'H' | 'E' | null;
type SortDirection = 'asc' | 'desc';

interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
}

interface ScoringResultRow {
  CarNumber: number;
  Institution: string;
  TeamName: string | null;
  VehicleClass: ScoringClass;
  OnTrackLaps: number | null;
  CorrectedTime: string | null;
  AvgLapTime: string | null;
  TotalPenaltyTime: string | null;
  TotalPenaltyPoints: string | null;
  FinalScore: string | null;
  ClassRank: number | null;
  LapSum: number | null;
  LapSumMax: number | null;
  DistanceFactor: string | null;
  MaxAvgLapTime: string | null;
  Tmin: string | null;
  SpeedFactor: string | null;
  PerformancePoints: string | null;
  StartPoints: string | null;
  ParticipationPoints: string | null;
  RawScore: string | null;
}

interface LapSummaryRow {
  CarNumber: number;
  TotalLaps: number;
  OnTrackLaps: number;
  FreePitLaps: number;
  ChargedPitLaps: number;
  InvalidLaps: number;
  ExcessLaps: number;
  DriverChanges: number;
  FastestLap: string | null;
  SlowestLap: string | null;
  AvgOnTrackLap: string | null;
}

interface PenaltySummaryRow {
  CarNumber: number;
  TotalCount: number;
  TotalTimePenalty: string | null;
  TotalPointPenalty: string | null;
}

type ResultSortKey =
  | 'ClassRank'
  | 'CarNumber'
  | 'TeamInstitution'
  | 'VehicleClass'
  | 'OnTrackLaps'
  | 'CorrectedTime'
  | 'AvgLapTime'
  | 'TotalPenaltyTime'
  | 'TotalPenaltyPoints'
  | 'FinalScore';

const RESULT_COLUMNS: {
  label: string;
  sortKey: ResultSortKey;
  align?: 'right';
}[] = [
  { label: 'Rank', sortKey: 'ClassRank' },
  { label: 'Car', sortKey: 'CarNumber' },
  { label: 'Team / Institution', sortKey: 'TeamInstitution' },
  { label: 'Class', sortKey: 'VehicleClass' },
  { label: 'Laps', sortKey: 'OnTrackLaps', align: 'right' },
  { label: 'Corrected', sortKey: 'CorrectedTime', align: 'right' },
  { label: 'Avg Lap', sortKey: 'AvgLapTime', align: 'right' },
  { label: 'Penalty Time', sortKey: 'TotalPenaltyTime', align: 'right' },
  { label: 'Penalty Pts', sortKey: 'TotalPenaltyPoints', align: 'right' },
  { label: 'Final Score', sortKey: 'FinalScore', align: 'right' },
];

const FIELD_DEFINITIONS: Record<string, string> = {
  Rank: 'Class rank after final scoring. Lower rank is better.',
  Car: 'The car number assigned to the team.',
  'Team / Institution': 'The team name when available, plus the institution associated with the car.',
  Class: 'Vehicle class used for scoring and ranking.',
  Laps: 'Scored on-track laps, excluding free pit laps and laps outside the 120-minute event time window.',
  Corrected: 'Corrected total time after subtracting free pit time and applying time penalties.',
  'Avg Lap': 'Corrected time divided by scored on-track laps.',
  'Penalty Time': 'Total time penalties, in seconds, applied to corrected time.',
  'Penalty Pts': 'Total point penalties applied after raw score calculation.',
  'Final Score': 'Final endurance score after performance, start, participation, and penalty points.',
  Result: 'Final scoring output for this car.',
  'Lap Breakdown': 'Lap-count and lap-time details used to audit this car’s scoring inputs.',
  'Scoring Audit': 'Intermediate scoring formula values used to produce the final score.',
  'Corrected Time': 'Corrected total time after subtracting free pit time and applying time penalties.',
  Total: 'Total lap rows for this car in the scoring lap summary.',
  'On Track': 'Valid laps classified as normal racing laps.',
  'Free Pit': 'Pit laps that are excluded from corrected racing time.',
  'Charged Pit': 'Pit laps that count against the team instead of being free.',
  'Driver Changes': 'Completed driver-change pit events compared with the required count.',
  'Out of Time Window': 'Lap rows that ended after the 120-minute event time window. They remain visible for audit but do not score.',
  'Excess Race Laps': 'Valid on-track laps after the race-distance limit. They remain visible for audit but do not score.',
  Fastest: 'Fastest valid on-track lap for this car.',
  Slowest: 'Slowest valid on-track lap for this car.',
  'Avg On Track': 'Average of valid on-track lap times before scoring penalties.',
  Penalties: 'Total number of penalties for this car.',
  'Penalty Points': 'Total point penalties applied after raw score calculation.',
  LapSum: 'Number of counted laps compared with the maximum lap count used for the distance factor.',
  'Distance Factor': 'Distance adjustment based on counted laps versus the maximum counted laps.',
  'Speed Factor': 'Speed adjustment based on average lap time between Tmin and MaxAvgLapTime.',
  'Raw Score': 'Score before final point penalties are applied.',
};

function DefinedLabel({ label, definitionKey = label }: { label: string; definitionKey?: string }) {
  const title = FIELD_DEFINITIONS[definitionKey];
  if (!title) return <>{label}</>;
  return (
    <Tooltip title={title} arrow>
      <Box component="span" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
        {label}
      </Box>
    </Tooltip>
  );
}

function num(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function formatLongTime(v: string | number | null | undefined): string {
  const n = num(v);
  if (n == null) return '-';
  const hours = Math.floor(n / 3600);
  const mins = Math.floor((n - hours * 3600) / 60);
  const secs = n - hours * 3600 - mins * 60;
  const pad = (x: number) => x.toString().padStart(2, '0');
  const secStr = secs.toFixed(1).padStart(4, '0');
  if (hours > 0) return `${hours}:${pad(mins)}:${secStr}`;
  return `${mins}:${secStr}`;
}

function formatLapTime(v: string | number | null | undefined): string {
  const n = num(v);
  if (n == null || n <= 0) return '-';
  const mins = Math.floor(n / 60);
  const secs = n - mins * 60;
  return `${mins}:${secs.toFixed(1).padStart(4, '0')}`;
}

function formatNumber(v: string | number | null | undefined, digits = 2): string {
  const n = num(v);
  return n == null ? '-' : n.toFixed(digits);
}

function nextSortState<K extends string>(current: SortState<K>, key: K): SortState<K> {
  return {
    key,
    direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
  };
}

function compareNullableValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  const aNum = typeof a === 'number' ? a : typeof a === 'string' && a.trim() !== '' ? Number(a) : NaN;
  const bNum = typeof b === 'number' ? b : typeof b === 'string' && b.trim() !== '' ? Number(b) : NaN;
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function resultSortValue(row: ScoringResultRow, key: ResultSortKey): unknown {
  switch (key) {
    case 'TeamInstitution':
      return `${row.TeamName ?? ''} ${row.Institution}`;
    case 'VehicleClass':
      return row.VehicleClass;
    case 'ClassRank':
    case 'CarNumber':
    case 'OnTrackLaps':
      return row[key];
    default:
      return num(row[key]);
  }
}

function sortResults(rows: ScoringResultRow[], sort: SortState<ResultSortKey>): ScoringResultRow[] {
  const direction = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => direction * compareNullableValues(resultSortValue(a, sort.key), resultSortValue(b, sort.key)));
}

function usePublicScoringData() {
  const queryClient = useQueryClient();
  const resultsQuery = useQuery({
    queryKey: ['publicScoringResults'],
    queryFn: async () => (await axios.get<ScoringResultRow[]>(`${API_BASE}/api/scoring/results`)).data,
  });
  const lapSummaryQuery = useQuery({
    queryKey: ['publicLapSummary'],
    queryFn: async () => (await axios.get<LapSummaryRow[]>(`${API_BASE}/api/scoring/lapSummary`)).data,
  });
  const penaltySummaryQuery = useQuery({
    queryKey: ['publicPenaltySummary'],
    queryFn: async () => (await axios.get<PenaltySummaryRow[]>(`${API_BASE}/api/scoring/penaltySummary`)).data,
  });
  const allCarsQuery = useQuery({
    queryKey: ['publicAllCarsBoard'],
    queryFn: async () => (await axios.get<LiveBoardRow[]>(`${API_BASE}/api/scoring/allCarsBoard`)).data,
  });

  const onWsMessage = useCallback(
    (msg: { channel: string; message: unknown }) => {
      if (msg.channel !== 'scoring') return;
      const body = msg.message as { event?: string };
      if (body.event !== 'calculated') return;
      queryClient.invalidateQueries({ queryKey: ['publicScoringResults'] });
      queryClient.invalidateQueries({ queryKey: ['publicLapSummary'] });
      queryClient.invalidateQueries({ queryKey: ['publicPenaltySummary'] });
      queryClient.invalidateQueries({ queryKey: ['publicAllCarsBoard'] });
    },
    [queryClient],
  );
  useWebSocketHandler(['scoring'], onWsMessage);

  return {
    results: resultsQuery.data ?? [],
    laps: lapSummaryQuery.data ?? [],
    penalties: penaltySummaryQuery.data ?? [],
    cars: (allCarsQuery.data ?? []).slice().sort((a, b) => a.CarNumber - b.CarNumber),
    loading: resultsQuery.isLoading || lapSummaryQuery.isLoading || penaltySummaryQuery.isLoading,
    error: resultsQuery.error ?? lapSummaryQuery.error ?? penaltySummaryQuery.error,
  };
}

function CellHeader({
  column,
  sort,
  onSort,
}: {
  column: (typeof RESULT_COLUMNS)[number];
  sort: SortState<ResultSortKey>;
  onSort: (key: ResultSortKey) => void;
}) {
  return (
    <TableCell
      align={column.align}
      sx={{ fontWeight: 600, bgcolor: 'dimgrey', color: 'white' }}
      sortDirection={sort.key === column.sortKey ? sort.direction : false}
    >
      <TableSortLabel
        active={sort.key === column.sortKey}
        direction={sort.key === column.sortKey ? sort.direction : 'asc'}
        onClick={() => onSort(column.sortKey)}
        sx={{
          color: 'inherit',
          '&:hover': { color: 'inherit' },
          '&.Mui-active': { color: 'inherit' },
          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
        }}
      >
        <DefinedLabel label={column.label} />
      </TableSortLabel>
    </TableCell>
  );
}

export default function PublicLeaderboard() {
  const navigate = useNavigate();
  const { results, loading, error } = usePublicScoringData();
  const [sort, setSort] = useState<SortState<ResultSortKey>>({ key: 'ClassRank', direction: 'asc' });
  const sortedRows = useMemo(() => sortResults(results, sort), [results, sort]);

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', p: 1, boxSizing: 'border-box' }}>
      <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Endurance Leaderboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click a car to view its scoring details.
          </Typography>
        </Box>
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {RESULT_COLUMNS.map((column) => (
                  <CellHeader
                    key={column.sortKey}
                    column={column}
                    sort={sort}
                    onSort={(key) => setSort((current) => nextSortState(current, key))}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={RESULT_COLUMNS.length} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={RESULT_COLUMNS.length} align="center" sx={{ color: 'error.main' }}>
                    Failed to load leaderboard.
                  </TableCell>
                </TableRow>
              ) : sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={RESULT_COLUMNS.length} align="center" sx={{ color: 'text.secondary' }}>
                    No scoring results are available yet.
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map((row) => (
                  <TableRow
                    key={row.CarNumber}
                    hover
                    onClick={() => navigate(`/${row.CarNumber}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{row.ClassRank ?? '-'}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.CarNumber}</TableCell>
                    <TableCell>
                      <Stack spacing={0}>
                        {row.TeamName && <Typography variant="body2">{row.TeamName}</Typography>}
                        <Typography variant="caption" color="text.secondary">
                          {row.Institution}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{row.VehicleClass ?? '-'}</TableCell>
                    <TableCell align="right">{row.OnTrackLaps ?? '-'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                      {formatLongTime(row.CorrectedTime)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                      {formatLapTime(row.AvgLapTime)}
                    </TableCell>
                    <TableCell align="right">{formatNumber(row.TotalPenaltyTime, 1)}</TableCell>
                    <TableCell align="right">{formatNumber(row.TotalPenaltyPoints, 1)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                      {formatNumber(row.FinalScore, 2)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

function DetailItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary">
        <DefinedLabel label={label} />
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
        {value}
      </Typography>
    </Stack>
  );
}

export function PublicCarDetail() {
  const { CarNumber } = useParams();
  const navigate = useNavigate();
  const carNumber = Number(CarNumber);
  const { results, laps, penalties, cars, loading, error } = usePublicScoringData();
  const row = results.find((r) => r.CarNumber === carNumber);
  const lap = laps.find((l) => l.CarNumber === carNumber);
  const penalty = penalties.find((p) => p.CarNumber === carNumber);
  const selectedCar = cars.find((car) => car.CarNumber === carNumber) ?? null;
  const carFilter = createFilterOptions<LiveBoardRow>({
    stringify: (car) => `${car.CarNumber} ${car.Institution}`,
    trim: true,
    ignoreCase: true,
  });

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', p: 2, boxSizing: 'border-box' }}>
      <Stack spacing={2}>
        <Autocomplete
          sx={{ maxWidth: 420 }}
          options={cars}
          getOptionLabel={(car) => `${car.CarNumber} — ${car.Institution}`}
          filterOptions={carFilter}
          isOptionEqualToValue={(a, b) => a.CarNumber === b.CarNumber}
          value={selectedCar}
          onChange={(_e, value) => {
            if (value) navigate(`/${value.CarNumber}`);
          }}
          autoHighlight
          openOnFocus
          selectOnFocus
          renderInput={(params) => (
            <TextField {...params} label="Select car" placeholder="Search car number or institution" />
          )}
        />

        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Typography color="error">Failed to load car details.</Typography>
        ) : !row ? (
          <Typography color="text.secondary">No scoring details found for car {CarNumber}.</Typography>
        ) : (
          <>
            <Card>
              <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Car {row.CarNumber}
                </Typography>
                <Typography color="text.secondary">
                  {row.TeamName ? `${row.TeamName} · ` : ''}{row.Institution}
                </Typography>
              </CardContent>
            </Card>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom><DefinedLabel label="Result" /></Typography>
                  <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                    <DetailItem label="Class" value={row.VehicleClass ?? '-'} />
                    <DetailItem label="Rank" value={row.ClassRank ?? '-'} />
                    <DetailItem label="Final Score" value={formatNumber(row.FinalScore, 2)} />
                    <DetailItem label="Corrected Time" value={formatLongTime(row.CorrectedTime)} />
                    <DetailItem label="Avg Lap" value={formatLapTime(row.AvgLapTime)} />
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom><DefinedLabel label="Lap Breakdown" /></Typography>
                  <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                    <DetailItem label="Total" value={lap?.TotalLaps ?? '-'} />
                    <DetailItem label="On Track" value={lap?.OnTrackLaps ?? row.OnTrackLaps ?? '-'} />
                    <DetailItem label="Free Pit" value={lap?.FreePitLaps ?? '-'} />
                    <DetailItem label="Charged Pit" value={lap?.ChargedPitLaps ?? '-'} />
                    <DetailItem label="Driver Changes" value={`${lap?.DriverChanges ?? 0} / 3`} />
                    <DetailItem label="Out of Time Window" value={lap?.InvalidLaps ?? 0} />
                    <DetailItem label="Excess Race Laps" value={lap?.ExcessLaps ?? 0} />
                    <DetailItem label="Fastest" value={formatLapTime(lap?.FastestLap)} />
                    <DetailItem label="Slowest" value={formatLapTime(lap?.SlowestLap)} />
                    <DetailItem label="Avg On Track" value={formatLapTime(lap?.AvgOnTrackLap)} />
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom><DefinedLabel label="Scoring Audit" /></Typography>
                  <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                    <DetailItem label="Penalties" value={penalty?.TotalCount ?? 0} />
                    <DetailItem label="Penalty Time" value={`${formatNumber(row.TotalPenaltyTime, 2)} s`} />
                    <DetailItem label="Penalty Points" value={formatNumber(row.TotalPenaltyPoints, 2)} />
                    <DetailItem label="LapSum" value={`${row.LapSum ?? '-'} / ${row.LapSumMax ?? '-'}`} />
                    <DetailItem label="Distance Factor" value={formatNumber(row.DistanceFactor, 4)} />
                    <DetailItem label="Speed Factor" value={formatNumber(row.SpeedFactor, 4)} />
                    <DetailItem label="Raw Score" value={formatNumber(row.RawScore, 2)} />
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}
